import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowsLeftRightIcon as ArrowsLeftRight, CheckCircleIcon as CheckCircle, WarningIcon as Warning, ShieldCheckIcon as ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatApiError, formatDate } from "@/lib/utils-app";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";

const TABS = [
  { key: "all", label: "All", filter: null },
  { key: "held", label: "In escrow", filter: "held" },
  { key: "disputed", label: "Disputed", filter: "disputed" },
  { key: "released", label: "Released", filter: "released" },
  { key: "refunded", label: "Refunded", filter: "refunded" },
];

export default function AdminEscrow() {
  const { user } = useAuth();
  const [tab, setTab] = useState("disputed");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async (filter) => {
    setLoading(true);
    try {
      const url = filter ? `/admin/escrow?status=${filter}` : "/admin/escrow";
      const r = await api.get(url);
      setRows(r.data?.rows || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = TABS.find((x) => x.key === tab);
    load(t?.filter);
  }, [tab]);

  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const runSweep = async () => {
    try {
      const r = await api.post("/admin/escrow/auto-release");
      toast.success(`Auto-released ${r.data?.released || 0} of ${r.data?.scanned || 0} eligible.`);
      load(TABS.find((x) => x.key === tab)?.filter);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const runDisputeSweep = async () => {
    try {
      const r = await api.post("/admin/escrow/auto-resolve-disputes");
      toast.success(`Auto-refunded ${r.data?.refunded || 0} of ${r.data?.scanned || 0} disputes ≥30 days old.`);
      load(TABS.find((x) => x.key === tab)?.filter);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <AppShell>
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-2">Admin · Money</p>
          <h1 className="font-spectral text-4xl tracking-tight flex items-center gap-2">
            <ArrowsLeftRight size={28} weight="duotone" /> Escrow & sales
          </h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={runSweep} className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white" data-testid="admin-escrow-auto-release-btn">
            Run auto-release sweep
          </Button>
          <Button onClick={runDisputeSweep} variant="outline" className="rounded-full" data-testid="admin-escrow-auto-resolve-btn">
            Run 30-day dispute auto-resolve
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          {TABS.map((t) => <TabsTrigger key={t.key} value={t.key} data-testid={`admin-escrow-tab-${t.key}`}>{t.label}</TabsTrigger>)}
        </TabsList>
        <TabsContent value={tab} className="mt-5">
          {loading ? (
            <div className="text-[#6B6B6B]">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="bg-white border border-dashed border-[#E5E5E5] rounded-xl p-12 text-center text-[#6B6B6B]">
              No rows match this filter.
            </div>
          ) : (
            <div className="space-y-3">
              {rows.map((r) => (
                <div key={r.id} className="bg-white border border-[#E5E5E5] rounded-xl p-5" data-testid={`admin-escrow-row-${r.id}`}>
                  <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                    <h3 className="font-spectral text-lg">{r.dog_name || "Dog"} <span className="text-[#6B6B6B] text-sm font-manrope">· ${r.amount?.toFixed(2)} · {r.status}</span></h3>
                    <span className="text-xs text-[#6B6B6B]">{formatDate(r.created_at)}</span>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2 text-sm text-[#6B6B6B]">
                    <div>Buyer: <span className="text-ink">{r.buyer_name || r.buyer_email}</span></div>
                    <div>Seller user: <span className="font-mono text-xs">{r.seller_user_id?.slice(0, 8)}…</span></div>
                  </div>
                  {r.dispute_reason && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-3 text-sm">
                      <strong className="text-red-900">Dispute:</strong> {r.dispute_reason}
                    </div>
                  )}

                  {/* Buyer-consent audit — surfaces the timestamp & breed warning the buyer ticked before paying. */}
                  <ConsentAudit row={r} />

                  <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-[#E5E5E5]">
                    {/* Admin discretion was removed under Path 1 venue
                        posture (Iter 204). Disputed escrows resolve via
                        mutual consent between buyer and seller, or via
                        the 30-day auto-refund timer. The buttons that
                        used to live here would now 403/410 — we surface
                        a read-only status pill instead.

                        Admins retain the ability to GRANT BUYER
                        EXTENSION REQUESTS (separate endpoint, not a
                        discretionary refund) and to view the consent
                        proposal trail. */}
                    {r.status === "disputed" && (
                      <span
                        className="inline-flex items-center gap-1.5 text-[12px] text-amber-900 bg-amber-50 border border-amber-200 rounded-full px-3 py-1"
                        data-testid={`admin-escrow-pending-${r.id}`}
                      >
                        <Warning size={12} weight="duotone" />
                        Awaiting mutual-consent resolution or 30-day auto-refund
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}

/**
 * Surface buyer-consent audit info on an escrow row so admins reviewing
 * a dispute can see at a glance:
 *   • whether the buyer ticked the general "I read the buyer checklist" box
 *   • whether the listing carried a restricted-breed warning at purchase
 *   • the server-side acknowledgment timestamp (best evidence for dispute
 *     defence; recorded in `buyer_consents` collection)
 *
 * Three states:
 *   1. Green pill — both audit row + escrow_consent payload are present.
 *   2. Amber pill — escrow row carries `buyer_consent` but no
 *      server-audit record was found (older purchase or storage failure).
 *   3. Grey pill — no consent data at all (legacy row, pre-launch).
 */
function ConsentAudit({ row }) {
  const audit = row?.buyer_consent_record;
  const inline = row?.buyer_consent;
  const has_any = Boolean(audit) || Boolean(inline);
  if (!has_any) {
    return (
      <div
        className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-[#6B6B6B] bg-[#F5F5F5] border border-[#E5E5E5] rounded-full px-2 py-1"
        data-testid={`admin-escrow-consent-none-${row.id}`}
      >
        <ShieldCheck size={11} weight="duotone" />
        No consent record on file (legacy purchase)
      </div>
    );
  }

  const breed_warning = audit?.breed_warning || inline?.breed_warning || null;
  const ack_at = audit?.acknowledged_at || null;
  const verified = Boolean(audit);
  const tone = verified ? "green" : "amber";
  const palette = tone === "green"
    ? { bg: "#F0F8F1", fg: "#0F6B2F", border: "#0F8043" }
    : { bg: "#FFF8E5", fg: "#8A6A00", border: "#C9A227" };

  return (
    <div
      className="mt-3 rounded-lg border p-3 text-[12px] leading-relaxed"
      style={{ background: palette.bg, color: palette.fg, borderColor: `${palette.border}66` }}
      data-testid={`admin-escrow-consent-${row.id}`}
    >
      <div className="flex items-start gap-2">
        <ShieldCheck size={14} weight="fill" style={{ color: palette.border }} className="mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold">
            {verified ? "Consent acknowledged" : "Consent claimed (no server-side audit row)"}
          </p>
          <ul className="mt-1 space-y-0.5">
            <li>
              <span className="opacity-75">Acknowledged at:</span>{" "}
              <span className="font-mono">
                {ack_at ? new Date(ack_at).toLocaleString() : "(only client-side claim recorded)"}
              </span>
            </li>
            {breed_warning ? (
              <li>
                <span className="opacity-75">Restricted-breed disclaimer:</span>{" "}
                <span data-testid={`admin-escrow-consent-breed-${row.id}`}>{breed_warning}</span>
              </li>
            ) : (
              <li>
                <span className="opacity-75">Breed warning:</span> none — unrestricted listing.
              </li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}
